/**
 * ColumnsMenu — popover for column visibility / freeze / drag-reorder.
 * Uses @dnd-kit for accessible drag handles.
 */
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Columns, GripVertical, Pin, PinOff } from "lucide-react";
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function ColumnsMenu({ state, tableKey }) {
  const { columns, setColumns, setColumnVisibility, toggleFrozen } = state;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = columns.findIndex((c) => c.key === active.id);
    const to = columns.findIndex((c) => c.key === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(columns, from, to).map((c, i) => ({ ...c, order: i }));
    setColumns(next);
  };

  const visibleCount = columns.filter((c) => c.visible !== false).length;
  const totalCount = columns.length;
  const allChecked = visibleCount === totalCount && totalCount > 0;
  const someChecked = visibleCount > 0 && visibleCount < totalCount;
  const setAll = (on) => {
    // R148g · Feb 2026 — Select all / Unselect all shortcut in the
    // column picker. Users no longer have to click each checkbox when
    // resetting a view or auditing what's available.
    for (const c of columns) setColumnVisibility(c.key, on);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs hover:bg-muted rounded-none"
          data-testid={`columns-menu-btn-${tableKey}`}
        >
          <Columns className="w-3.5 h-3.5" /> Columns ({visibleCount})
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0 rounded-none" data-testid={`columns-menu-${tableKey}`}>
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <div className="label-eyebrow">Show, reorder, freeze</div>
          <p className="text-[10px] text-muted-foreground mt-0.5">Drag to reorder. Freeze pins columns to the left edge.</p>
        </div>
        {/* R148g · Bulk select / unselect all */}
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border bg-muted/10" data-testid={`columns-bulk-toggle-${tableKey}`}>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => { if (el) el.indeterminate = someChecked; }}
              onChange={(e) => setAll(e.target.checked)}
              data-testid={`columns-select-all-${tableKey}`}
              aria-label={allChecked ? "Unselect all columns" : "Select all columns"}
            />
            <span className="font-medium">{allChecked ? "Unselect all" : "Select all"}</span>
            <span className="text-muted-foreground text-[10px]">({visibleCount}/{totalCount})</span>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setAll(true)}
              className="text-[10px] uppercase tracking-widest text-primary hover:underline"
              data-testid={`columns-select-all-btn-${tableKey}`}
            >All</button>
            <span className="text-muted-foreground/40">·</span>
            <button
              type="button"
              onClick={() => setAll(false)}
              className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive"
              data-testid={`columns-unselect-all-btn-${tableKey}`}
            >None</button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={columns.map((c) => c.key)} strategy={verticalListSortingStrategy}>
              {columns.map((c) => (
                <SortableRow
                  key={c.key}
                  col={c}
                  onToggleVisibility={(v) => setColumnVisibility(c.key, v)}
                  onToggleFrozen={() => toggleFrozen(c.key)}
                  testKey={c.key}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SortableRow({ col, onToggleVisibility, onToggleFrozen, testKey }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1.5 border-b border-border last:border-b-0 hover:bg-muted/30"
      data-testid={`columns-row-${testKey}`}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab active:cursor-grabbing p-1"
        aria-label={`Reorder ${col.label || col.key}`}
        data-testid={`columns-drag-${testKey}`}
      >
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      <input
        type="checkbox"
        checked={col.visible !== false}
        onChange={(e) => onToggleVisibility(e.target.checked)}
        data-testid={`columns-visible-${testKey}`}
      />
      <span className="flex-1 truncate text-sm">{col.label || col.key}</span>
      <button
        type="button"
        onClick={onToggleFrozen}
        className={`p-1 rounded ${col.frozen ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
        title={col.frozen ? "Unfreeze" : "Freeze to left"}
        data-testid={`columns-freeze-${testKey}`}
      >
        {col.frozen ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
